import React, { useState } from 'react';

import FileUpload from './FileUpload';

import Button from '~/core/atoms/Buttons/Button';
import TextButton from '~/core/atoms/Buttons/TextButton';
import BaseDialog from '~/core/molecules/Dialog/BaseDialog';
import { FileUploadResponse } from '~/core/molecules/FileUpload/types';

export interface UploadedFile {
  name: string;
  url: string;
  type: string;
  size: number;
  document_external_id?: string;
}

export interface FileUploadModalProps {
  isOpen: boolean;
  uploadUrl: string;
  onClose: () => void;
  onSubmit: (files: UploadedFile[]) => void;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({ isOpen, onClose, uploadUrl, onSubmit }) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const handleUploadSuccess = (response: FileUploadResponse, file: File) => {
    const [savedFile] = response.saved_files || [];
    if (!savedFile) {
      console.error('No saved file returned from upload response');
      return;
    }

    const uploadedFile: UploadedFile = {
      name: file.name,
      url: savedFile.signed_url,
      type: file.type,
      size: file.size,
      document_external_id: savedFile.document_external_id,
    };
    setUploadedFiles((prev) => [...prev, uploadedFile]);
  };

  const handleFileRemove = (file: File) => {
    setUploadedFiles((prev) => prev.filter((f) => f.name !== file.name));
  };

  const handleSubmit = async () => {
    // Just submit the uploaded files - AI processing will be handled by the parent component
    await onSubmit(uploadedFiles);
    onClose();
  };

  const handleClose = () => {
    setUploadedFiles([]); // Clear files when closing
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Files"
      actions={
        <div className="flex items-center justify-end gap-12">
          <TextButton onClick={handleClose} className="text-base font-normal text-slate-800">
            Cancel
          </TextButton>
          <Button onClick={handleSubmit} size="sm" variant="primary" disabled={uploadedFiles.length === 0}>
            Done
          </Button>
        </div>
      }
    >
      <FileUpload uploadUrl={uploadUrl} onUploadSuccess={handleUploadSuccess} onFileRemove={handleFileRemove} />
    </BaseDialog>
  );
};

export default FileUploadModal;
